import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Tip } from '../../../../components/Tip';

interface ProgressIndicatorProps {
    currentStepIndex: number;
    totalSteps: number;
    tooltipId?: string;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ 
    currentStepIndex, 
    totalSteps,
    tooltipId = "progress-dot-tooltip"
}) => {
    const { t } = useTranslation('content');
    
    if (totalSteps === 0) return null;
    
    // Current step index is 0-based, but we want to show progress including the current step
    // So we add 1 to get the number of steps to fill (steps 1 to currentStepIndex+1)
    const stepsToFill = currentStepIndex + 1;
    
    // Limit the number of dots to display (max 12)
    const maxDots = 12;
    const shouldCondense = totalSteps > maxDots;
    const visibleDots = shouldCondense ? maxDots : totalSteps;
    
    // Calculate the progress percentage
    const progressPercentage = stepsToFill / totalSteps;
    
    // Create an array of dots
    const dots = [];
    for (let i = 0; i < visibleDots; i++) {
        // If condensed, distribute the filled dots proportionally
        const isFilled = shouldCondense 
            ? i < Math.round(progressPercentage * maxDots)
            : i < stepsToFill;
            
        dots.push(
            <ProgressDot 
                key={i} 
                isFilled={isFilled}
                isCurrentStep={shouldCondense ? false : i === currentStepIndex}
                data-tooltip-id={tooltipId}
                data-tooltip-content={
                    shouldCondense
                        ? `${Math.round(progressPercentage * 100)}% ${t('progress.completed', 'completed')}`
                        : `${t('progress.step', 'Step')} ${i + 1}${i === currentStepIndex ? ' (current)' : ''}`
                }
            />
        );
    }
    
    return (
        <ProgressIndicatorContainer>
            {dots}
            <Tip id={tooltipId} />
        </ProgressIndicatorContainer>
    );
};

const ProgressIndicatorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    margin-top: 4px;
    padding: 2px 0;
`;

const ProgressDot = styled.div<{ isFilled: boolean; isCurrentStep?: boolean }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => {
        if (props.isCurrentStep) return props.theme.color.electricBlue;
        return props.isFilled 
            ? 'rgba(255, 255, 255, 0.7)' 
            : 'rgba(255, 255, 255, 0.2)';
    }};
    transition: background-color 0.3s ease;
    box-shadow: ${props => props.isCurrentStep ? '0 0 5px rgba(0, 162, 255, 0.8)' : 'none'};
    
    &:hover {
        transform: scale(1.2);
        transition: transform 0.2s ease;
    }
`; 